import { cn } from "../../lib/cn";

const PERSONAS = [
  {
    role: "Engineering",
    outcome:
      "Stack-aware fix steps tied to affected files, ready for a ticket or PR description.",
    visual: "code",
  },
  {
    role: "SEO & growth",
    outcome:
      "Track Core Web Vitals across runs and show progress after optimizations ship.",
    visual: "vitals",
  },
  {
    role: "Agencies",
    outcome:
      "Monitor client URLs in-app or export results to Google Drive for recurring reporting.",
    visual: "clients",
  },
  {
    role: "Product & ops",
    outcome:
      "Keep checkout, landing pages, and critical flows within acceptable score ranges.",
    visual: "flows",
  },
];

function CodeVisual() {
  return (
    <div className="space-y-2" aria-hidden>
      {["app/layout.tsx", "components/Hero.tsx", "public/gtm.js"].map((file) => (
        <div
          key={file}
          className="rounded-md border border-border/70 bg-muted/50 px-2.5 py-1.5 text-[10px] font-mono text-muted-foreground"
        >
          {file}
        </div>
      ))}
    </div>
  );
}

function VitalsVisual() {
  return (
    <div className="relative min-h-[5.5rem]" aria-hidden>
      <div className="flex flex-wrap gap-1.5">
        {["LCP", "INP", "CLS"].map((label) => (
          <span
            key={label}
            className="rounded border border-border/60 bg-background/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-muted-foreground"
          >
            {label}
          </span>
        ))}
      </div>
      <svg
        viewBox="0 0 200 100"
        className="absolute top-0 right-0 w-[min(72%,200px)] text-brand/30"
        fill="none"
      >
        <path
          d="M8 78 L48 62 L88 68 L128 34 L168 22 L192 14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {[
          [48, 62],
          [88, 68],
          [128, 34],
          [168, 22],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="3.5" fill="currentColor" />
        ))}
      </svg>
    </div>
  );
}

function ClientsVisual() {
  return (
    <div className="grid grid-cols-2 gap-1.5 sm:gap-2" aria-hidden>
      {["client-a.com", "client-b.io", "client-c.app", "client-d.co"].map(
        (domain) => (
          <div
            key={domain}
            className="rounded-lg border border-border/70 bg-muted/40 px-2 py-1.5"
          >
            <p className="truncate text-[10px] font-mono text-brand/85">
              {domain}
            </p>
            <p className="mt-0.5 text-[9px] text-muted-foreground tabular-nums">
              92 / 88
            </p>
          </div>
        ),
      )}
    </div>
  );
}

function FlowsVisual() {
  return (
    <div className="flex items-center pt-1" aria-hidden>
      {["Landing", "Checkout", "Confirm"].map((step, i) => (
        <div key={step} className="flex flex-1 items-center min-w-0">
          <div className="rounded-full border border-border bg-background/80 px-2.5 py-1 text-[9px] font-mono text-muted-foreground whitespace-nowrap">
            {step}
          </div>
          {i < 2 && <span className="mx-1 h-px flex-1 bg-border/80" />}
        </div>
      ))}
    </div>
  );
}

const VISUALS = {
  code: CodeVisual,
  vitals: VitalsVisual,
  clients: ClientsVisual,
  flows: FlowsVisual,
};

function PersonaCard({ role, outcome, visual, tall = false, className }) {
  const Visual = VISUALS[visual];

  return (
    <article
      className={cn(
        "group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card",
        "transition-colors hover:border-foreground/15 hover:bg-muted/15",
        className,
      )}
    >
      <div
        className={cn(
          "shrink-0 px-5 pt-5 sm:px-6 sm:pt-6",
          tall && "flex-1",
        )}
      >
        <Visual />
      </div>

      <div className="mt-auto shrink-0 px-5 py-4 sm:px-6 sm:py-5">
        <h3 className="text-sm font-semibold text-foreground">{role}</h3>
        <p className="mt-1.5 max-w-md text-sm leading-relaxed text-muted-foreground">
          {outcome}
        </p>
      </div>
    </article>
  );
}

export function PersonaBento() {
  return (
    <section className="py-16 sm:py-20">
      <h2 className="text-2xl sm:text-3xl font-[450] text-foreground tracking-[-0.06em] max-w-xl">
        Built for people responsible for speed
      </h2>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-3 sm:gap-4">
        <PersonaCard
          {...PERSONAS[0]}
          tall
          className="sm:col-span-2 lg:col-span-1 lg:row-span-2 lg:min-h-[24rem]"
        />
        <PersonaCard
          {...PERSONAS[1]}
          className="min-h-[13.5rem] lg:min-h-[12.5rem]"
        />
        <PersonaCard
          {...PERSONAS[2]}
          className="min-h-[13.5rem] lg:min-h-[12.5rem]"
        />
        <PersonaCard
          {...PERSONAS[3]}
          className="sm:col-span-2 lg:col-span-2 min-h-[11.5rem] lg:min-h-[11.5rem]"
        />
      </div>
    </section>
  );
}
